import React, { useEffect, useState, createContext, useContext } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface SecurityEvent {
  timestamp: string;
  type: string;
  severity?: string;
  data: Record<string, unknown>;
}

interface SecurityAuditContextValue {
  auditAccessAttempt: (permission: string, granted: boolean, context?: Record<string, unknown>) => void;
  auditDataLoadFailure: (dataType: string, error: unknown, isCritical?: boolean) => void;
  getSecurityEvents: () => SecurityEvent[];
  hasSuspiciousActivity: () => boolean;
}

/**
 * COMPONENTE DE AUDITORIA DE SEGURANÇA
 *
 * Monitora e registra atividades de segurança críticas no sistema.
 * Funciona como uma camada adicional de proteção e observabilidade.
 */
export const SecurityAudit = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, securityStatus, authError } = useAuth();
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [suspiciousActivity, setSuspiciousActivity] = useState(false);

  // Monitorar mudanças críticas de segurança
  useEffect(() => {
    const event = {
      timestamp: new Date().toISOString(),
      type: 'AUTH_STATE_CHANGE',
      data: {
        isAuthenticated,
        securityStatus,
        hasAuthError: !!authError,
        userEmail: user?.email || 'anonymous'
      }
    };

    logSecurityEvent(event);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, securityStatus, authError, user]);

  // Detectar atividade suspeita
  useEffect(() => {
    const recentEvents = securityEvents.slice(-10);
    const authFailures = recentEvents.filter(e =>
      e.type === 'AUTH_STATE_CHANGE' && e.data.hasAuthError
    ).length;

    const rapidChanges = recentEvents.filter(e =>
      new Date().getTime() - new Date(e.timestamp).getTime() < 30000 // 30 segundos
    ).length;

    if (authFailures > 3 || rapidChanges > 15) {
      setSuspiciousActivity(true);
      logSecurityEvent({
        timestamp: new Date().toISOString(),
        type: 'SUSPICIOUS_ACTIVITY_DETECTED',
        severity: 'HIGH',
        data: {
          authFailures,
          rapidChanges,
          userEmail: user?.email || 'anonymous'
        }
      });
    } else {
      setSuspiciousActivity(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityEvents, user]);

  const logSecurityEvent = (event: SecurityEvent) => {
    // SECURITY: Implement secure event logging with debouncing to prevent loops
    // Use a ref to track if we're currently processing events
    if ((logSecurityEvent as unknown as { isProcessing?: boolean }).isProcessing) {
      return;
    }

    (logSecurityEvent as unknown as { isProcessing: boolean }).isProcessing = true;

    try {
      // Safe console logging with event type filtering
      if (import.meta.env.DEV &&
          event.type !== 'AUTH_STATE_CHANGE') { // Avoid logging frequent auth changes
        const sanitizedEvent = {
          type: event.type,
          timestamp: event.timestamp,
          severity: event.severity || 'INFO'
          // Exclude potentially sensitive data
        };
      }

      // Add to event history with proper debouncing
      setSecurityEvents(prev => {
        // Prevent duplicate events within 1 second
        const lastEvent = prev[prev.length - 1];
        if (lastEvent &&
            lastEvent.type === event.type &&
            new Date(event.timestamp).getTime() - new Date(lastEvent.timestamp).getTime() < 1000) {
          return prev; // Skip duplicate
        }

        return [...prev.slice(-99), event]; // Keep last 100 events
      });

      // Send to monitoring service for high-severity events
      if (import.meta.env.PROD && event.severity === 'HIGH') {
        // TODO: Implement proper external monitoring service integration
        // sendToMonitoringService(event);
      }
    } finally {
      // Reset processing flag after a short delay
      setTimeout(() => {
        (logSecurityEvent as unknown as { isProcessing: boolean }).isProcessing = false;
      }, 100);
    }
  };

  // Monitorar tentativas de acesso não autorizado
  const auditAccessAttempt = (permission: string, granted: boolean, context: Record<string, unknown> = {}) => {
    const event = {
      timestamp: new Date().toISOString(),
      type: 'ACCESS_ATTEMPT',
      severity: granted ? 'INFO' : 'MEDIUM',
      data: {
        permission,
        granted,
        userEmail: user?.email || 'anonymous',
        securityStatus,
        context
      }
    };

    logSecurityEvent(event);

    // Se acesso foi negado, incrementar contador de tentativas suspeitas
    if (!granted) {
      logSecurityEvent({
        timestamp: new Date().toISOString(),
        type: 'ACCESS_DENIED',
        severity: 'MEDIUM',
        data: {
          permission,
          userEmail: user?.email || 'anonymous',
          attempts: securityEvents.filter(e =>
            e.type === 'ACCESS_DENIED' &&
            e.data.userEmail === (user?.email || 'anonymous') &&
            new Date().getTime() - new Date(e.timestamp).getTime() < 300000 // últimos 5 minutos
          ).length + 1
        }
      });
    }
  };

  // Monitorar falhas de carregamento de dados críticos
  const auditDataLoadFailure = (dataType: string, error: unknown, isCritical = false) => {
    const event = {
      timestamp: new Date().toISOString(),
      type: 'DATA_LOAD_FAILURE',
      severity: isCritical ? 'HIGH' : 'MEDIUM',
      data: {
        dataType,
        error: (error as { message?: string })?.message || 'Unknown error',
        userEmail: user?.email || 'anonymous',
        securityStatus
      }
    };

    logSecurityEvent(event);
  };

  // Validar integridade da sessão periodicamente
  useEffect(() => {
    if (!isAuthenticated) return;

    const validateSession = () => {
      if (user && !user.email) {
        logSecurityEvent({
          timestamp: new Date().toISOString(),
          type: 'SESSION_INTEGRITY_VIOLATION',
          severity: 'HIGH',
          data: {
            reason: 'User without email',
            userId: user.id
          }
        });
      }

      if (securityStatus === 'error') {
        logSecurityEvent({
          timestamp: new Date().toISOString(),
          type: 'SECURITY_STATUS_ERROR',
          severity: 'HIGH',
          data: {
            authError,
            userEmail: user?.email || 'anonymous'
          }
        });
      }
    };

    // Validar a cada 30 segundos
    const interval = setInterval(validateSession, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, securityStatus, authError]);

  // Expor funções de auditoria via contexto
  const auditContext = {
    auditAccessAttempt,
    auditDataLoadFailure,
    getSecurityEvents: () => securityEvents,
    hasSuspiciousActivity: () => suspiciousActivity
  };

  return (
    <SecurityAuditContext.Provider value={auditContext}>
      {children}
      {/* Indicador visual de atividade suspeita em desenvolvimento */}
      {import.meta.env.DEV && suspiciousActivity && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: '#ff4757',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 9999
        }}>
          ⚠️ Atividade Suspeita Detectada
        </div>
      )}
    </SecurityAuditContext.Provider>
  );
};

// Contexto para auditoria
const SecurityAuditContext = createContext<SecurityAuditContextValue | null>(null);

export const useSecurityAudit = () => {
  const context = useContext(SecurityAuditContext);
  if (!context) {
    // Retornar implementação vazia se usado fora do provider
    return {
      auditAccessAttempt: () => {},
      auditDataLoadFailure: () => {},
      getSecurityEvents: () => [],
      hasSuspiciousActivity: () => false
    };
  }
  return context;
};

export default SecurityAudit;